/**
 * ⚠️ BACKEND GAP — DRAFT VALUES, NOT PRODUCTION FIGURES.
 *
 * Voice rates and provider capacity, hardcoded so the runway and cost estimate
 * can be built before the endpoints that serve them exist (VOX-270).
 *
 * WHY THIS FILE EXISTS AT ALL, instead of the numbers living in `domain/`:
 * the PRD is explicit that "the estimate must be fed by REAL billing rates, so
 * that what is shown matches what is invoiced". Until that is true, every
 * currency figure this feature renders is a demo value — and a demo value
 * buried among real business logic is how "it worked with demo data" ships.
 * Keeping them here means one import to find, and one file to delete.
 *
 * WHAT REPLACES IT:
 *   PROVIDER_DAILY_CAP  → the agent's telephony provider daily capacity
 *   COST_PER_MINUTE     → the account's real per-minute voice rate (billing)
 *   MINUTES_PER_CONTACT → derived from the agent's own historical call data
 *
 * Until then `estimateCost()` must never be presented as a firm quote.
 */

/**
 * Calls a day the telephony provider sustains.
 *
 * Negotiated per account with the provider, which is exactly why it cannot stay
 * a constant: two accounts on the same plan do not share this number.
 */
export const PROVIDER_DAILY_CAP = 50_000;

/** Per-minute voice rate, in account currency. Comes from billing. */
export const COST_PER_MINUTE = 0.18;

/**
 * BILLABLE minutes per contact. Connected conversation only, because carriers
 * do not charge for ringing nobody answered.
 *
 *   2.4 attempts × 0.30 answer rate × 1.7 min          = 1.224  (call)
 *   2.4 × 0.30 × 0.60 right-party × 2.4 min            = 1.037  (extra when the
 *                                                                account holder
 *                                                                speaks)
 *                                                        ─────
 *                                                        2.26
 *
 * The factors are design assumptions — a plausible average,
 * not this account's measured behaviour.
 */
export const MINUTES_PER_CONTACT = 2.26;

/**
 * Estimated cost of calling `contacts` people.
 *
 * ⚠️ Draft figure. See the file header before showing this next to a currency
 * symbol without a qualifier.
 */
export function estimateCost(contacts: number): number {
  return Math.round(contacts * MINUTES_PER_CONTACT * COST_PER_MINUTE * 100) / 100;
}

/** Estimated billable minutes for `contacts` people. Same caveat as above. */
export function estimateMinutes(contacts: number): number {
  return Math.round(contacts * MINUTES_PER_CONTACT * 10) / 10;
}
